import { useState, useEffect, useCallback } from "react";
import { BrowserPrintDevice } from "src/types";

export function useZebraPrinter() {
  const [device, setDevice] = useState<BrowserPrintDevice | null>(null);
  const [availablePrinters, setAvailablePrinters] = useState<BrowserPrintDevice[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!window.BrowserPrint) {
      setError("Zebra Browser Print not installed");
      return;
    }

    // Get the user's default printer first (fast, no discovery needed)
    window.BrowserPrint.getDefaultDevice(
      "printer",
      (defaultDevice) => {
        if (defaultDevice && defaultDevice.uid) {
          setDevice(defaultDevice);
          setIsConnected(true);
        }
      },
      () => setError("Could not get default printer")
    );

    // Discover all local printers in parallel (slower, but gives full list)
    window.BrowserPrint.getLocalDevices(
      (devices) => setAvailablePrinters(devices),
      () => {},
      "printer"
    );
  }, []);

  const selectPrinter = useCallback(
    (uid: string) => {
      const selected = availablePrinters.find((d) => d.uid === uid);
      if (selected) {
        setDevice(selected);
        setIsConnected(true);
        setError(null);
      }
    },
    [availablePrinters]
  );

  const printZpl = useCallback(
    async (zpl: string): Promise<void> => {
      if (!device) {
        throw new Error("No printer connected");
      }

      return new Promise<void>((resolve, reject) => {
        device.send(
          zpl,
          () => resolve(),
          (err) => reject(new Error(err))
        );
      });
    },
    [device]
  );

  return { printer: device, availablePrinters, isConnected, error, selectPrinter, printZpl };
}
